import type React from 'react';

import { Box, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation-10';

import type { Section } from '../../../models/index';
import { PETS } from '../../../constants';
import { MAX_AMOUNT_ERROR_MESSAGE } from '../../../constants/stringConstants';
import { ValidationType } from '../../../utils/validators';
import PetsYesAndNoExpansion from './Pets/PetsYesAndNoExpansion';
import PetsInsuranceYesAndNo from './Pets/PetsInsuranceYesAndNo';
import CurrencyInput from '../../Widgets/CurrencyInput';

const BOUNDS = PETS.BOUNDS.V1;

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

const INITIAL_STATE = {
  pets_isEnabled: false,
  pets_requiresInsuranceCoverage: null,
  pets_rentAmountPerMonth: '',
  pets_nonRefundableFee: '',
  pets_refundableDeposit: '',
};

function Pets_v1({ section, setSectionValues }: Props): JSX.Element {
  function isDepositAmountValid(amount: string): boolean {
    if (Number(amount.replace(/,/g, '')) > BOUNDS.MAX_DEPOSIT_AMOUNT) {
      section
        .getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)
        .setErrorMessage(MAX_AMOUNT_ERROR_MESSAGE(BOUNDS.MAX_DEPOSIT_AMOUNT));
      return false;
    }
    return true;
  }
  section.setCustomFieldValidator(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, isDepositAmountValid);

  section.setValidation(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH, ValidationType.ZeroCurrencyValidator);
  section.setValidation(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, ValidationType.ZeroCurrencyValidator);
  section.setValidation(PETS.FIELD_NAME.NON_REFUNDABLE_FEE, ValidationType.ZeroCurrencyValidator);
  section.setFieldValue(
    PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH,
    section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH),
  );
  section.setFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT, section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT));
  section.setFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE, section.getFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE));

  function onChargesChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void {
    const { value } = event.currentTarget;
    const { fieldName } = event.currentTarget.dataset;

    setSectionValues({ [fieldName]: value });
  }

  function onNoPetsClick(): void {
    setSectionValues(INITIAL_STATE);
  }

  function onYesPetsClick(): void {
    setSectionValues({ pets_isEnabled: true });
  }

  function onNoInsuranceClick(): void {
    setSectionValues({ pets_requiresInsuranceCoverage: false });
  }

  function onYesInsuranceClick(): void {
    setSectionValues({ pets_requiresInsuranceCoverage: true });
  }

  return (
    <PetsYesAndNoExpansion
      yesHandler={onYesPetsClick}
      noHandler={onNoPetsClick}
      yesActive={section.getFieldValue(PETS.FIELD_NAME.IS_ENABLED)}
    >
      <Box css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}>
        <PetsInsuranceYesAndNo
          noHandler={onNoInsuranceClick}
          yesActive={section.getFieldValue(PETS.FIELD_NAME.REQUIRES_INSURANCE_COVERAGE)}
          yesHandler={onYesInsuranceClick}
        />
      </Box>
      <Box css={{ marginTop: 'layout.tight', marginBottom: 'layout.tight' }}>
        <Paragraph textStyle="body-sm-bold">Are there pet charges?</Paragraph>
        <Paragraph css={{ marginBottom: 'layout.tighter' }}>
          Fill in $0 if none. Typically you should only select one option.
        </Paragraph>
        <FormField
          required
          label={<Label>Pet rent</Label>}
          error={section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH).hasError}
          control={
            <CurrencyInput
              aria-label="Pet rent"
              data-field-name={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
              name={PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH}
              value={section.getFieldValue(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH)}
              onChange={onChargesChange}
              onBlur={onChargesChange}
              error={section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH).hasError}
              endAdornment="per month"
            />
          }
          description={
            section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH).hasError ? (
              <FormHelp>{section.getField(PETS.FIELD_NAME.RENT_AMOUNT_PER_MONTH).getErrorMessage()}</FormHelp>
            ) : (
              <FormHelp>Amount generally should not exceed $75 per pet</FormHelp>
            )
          }
        />
        <FormField
          required
          css={{ marginTop: 'layout.tighter' }}
          label={<Label>Non-refundable fee</Label>}
          error={section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).hasError}
          control={
            <CurrencyInput
              aria-label="Non-refundable fee"
              data-field-name={PETS.FIELD_NAME.NON_REFUNDABLE_FEE}
              name={PETS.FIELD_NAME.NON_REFUNDABLE_FEE}
              value={section.getFieldValue(PETS.FIELD_NAME.NON_REFUNDABLE_FEE)}
              onChange={onChargesChange}
              onBlur={onChargesChange}
              error={section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).hasError}
            />
          }
          description={
            section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).hasError && (
              <FormHelp>{section.getField(PETS.FIELD_NAME.NON_REFUNDABLE_FEE).getErrorMessage()}</FormHelp>
            )
          }
        />
        <FormField
          required
          css={{ marginTop: 'layout.tighter' }}
          label={<Label>Refundable deposit</Label>}
          error={section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).hasError}
          control={
            <CurrencyInput
              aria-label="Refundable deposit"
              data-field-name={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
              name={PETS.FIELD_NAME.REFUNDABLE_DEPOSIT}
              value={section.getFieldValue(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT)}
              onChange={onChargesChange}
              onBlur={onChargesChange}
              error={section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).hasError}
            />
          }
          description={
            <>
              {section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).hasError ? (
                <FormHelp>{section.getField(PETS.FIELD_NAME.REFUNDABLE_DEPOSIT).getErrorMessage()}</FormHelp>
              ) : (
                <FormHelp>A refundable pet deposit is treated as a security deposit</FormHelp>
              )}
            </>
          }
        />
      </Box>
    </PetsYesAndNoExpansion>
  );
}

Pets_v1.description = `Pets screen with bounds of ${JSON.stringify(BOUNDS)}`;

export default Pets_v1;
